'use client';

import Bugsnag from '@bugsnag/js';
import { createIntl } from '@formatjs/intl';
import type { userAgent } from 'next/server';
// eslint-disable-next-line @alltrails/custom/react-imports
import React, { Fragment, PropsWithChildren, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import setGlobalFetchHeaders from '@alltrails/core/utils/setGlobalFetchHeaders';
import { ListsState } from '@alltrails/lists/slices/lists';
import MapProvider from '@alltrails/maps/components/MapProvider/MapProvider';
import { CustomRouteState } from '@alltrails/maps/redux/customRoute';
import type { MapState } from '@alltrails/maps/redux/reducer';
import type { Bootstrap } from '@alltrails/networking/types/Bootstrap';
import { DatadogRumScript } from '@alltrails/stability';
import type Experiments from '@alltrails/types/Experiments';
import { setupListeners } from '@/utils/listenerMiddleware';
import { buildStore } from '../utils/redux';

type Props = {
  bootstrap: Bootstrap;
  browser: ReturnType<typeof userAgent>['browser'];
  experiments: Experiments;
  irclickid?: string;
  lists: ListsState;
  locale: ReturnType<typeof createIntl>['locale'];
  map: MapState;
  customRoute: CustomRouteState;
  messages: ReturnType<typeof createIntl>['messages'];
};

setupListeners();
const plugin = Bugsnag.getPlugin('react');
const ErrorBoundary = plugin ? plugin.createErrorBoundary(React) : Fragment;

export default function CustomProvider({
  bootstrap,
  browser,
  experiments,
  lists,
  map,
  customRoute,
  messages,
  locale,
  children,
  irclickid
}: PropsWithChildren<Props>) {
  const languageRegionCode = useLanguageRegionCode();
  setGlobalFetchHeaders({ languageRegionCode });
  // Need to memoize the store so that we can change the url params when the map moves without resetting the store each time
  const store = useMemo(
    () =>
      buildStore({
        algoliaConfigs: bootstrap.algoliaConfig,
        amplitudeAnalyticsSettings: bootstrap.amplitudeAnalyticsSettings,
        browser,
        context: bootstrap.context,
        currencyCode: bootstrap.currencyCode,
        experiments,
        features: bootstrap.features,
        irclickid,
        lists,
        map,
        customRoute,
        plans: bootstrap.plans,
        requestCountryCode: bootstrap.requestCountryCode
      }),
    [
      bootstrap.algoliaConfig,
      bootstrap.amplitudeAnalyticsSettings,
      bootstrap.context,
      bootstrap.currencyCode,
      bootstrap.features,
      bootstrap.plans,
      bootstrap.requestCountryCode,
      browser,
      customRoute,
      experiments,
      irclickid,
      lists,
      map
    ]
  );

  return (
    <ErrorBoundary>
      <DatadogRumScript />
      <IntlProvider messages={messages} locale={locale}>
        <Provider store={store}>
          <MapProvider>{children}</MapProvider>
        </Provider>
      </IntlProvider>
    </ErrorBoundary>
  );
}
